import { inject, Injectable, InjectionToken, Provider } from '@angular/core';
import {
  Translation,
  TRANSLOCO_INTERCEPTOR,
  TRANSLOCO_SCOPE,
  TranslocoInterceptor,
  TranslocoLoader,
} from '@jsverse/transloco';
import { HttpClient } from '@angular/common/http';
import { forkJoin, map } from 'rxjs';

export const TRANSLOCO_STATIC_SCOPE = new InjectionToken<undefined>('TRANSLOCO_STATIC_SCOPE');

export function provideStaticScope(scope: string): Provider {
  return {
    provide: TRANSLOCO_STATIC_SCOPE,
    useValue: { scope, alias: `${scope}` },
    multi: true,
  };
}

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  private readonly http = inject(HttpClient);
  private readonly staticModules: { scope: string; alias: string }[] = inject(TRANSLOCO_STATIC_SCOPE);

  getTranslation(langPath: string) {
    const paths = this.getPaths(langPath);
    return forkJoin(paths.map(x => this.http.get<Translation>(`/assets/i18n/${x}.json`))).pipe(
      map(x =>
        x.reduce((acc, curr) => {
          return { ...acc, ...curr };
        }, {}),
      ),
    );
  }

  private getPaths(langPath: string) {
    const [scope, lang] = langPath.split('/');
    if (!lang) {
      const paths = this.staticModules.map(x => (x.scope === '' ? `${scope}` : `${x.scope}/${scope}`));
      paths.push(scope);
      return paths;
    } else {
      return [langPath];
    }
  }
}

export class TranslocoScopeInterceptor implements TranslocoInterceptor {
  preSaveTranslation(translation: Translation, lang: string): Translation {
    Object.keys(translation).forEach(key => {
      if (key.startsWith('$')) {
        const value = translation[key];
        delete translation[key];
        translation[key.split('.').slice(1).join('.')] = value;
      }
    });

    return this.flattenObject(translation);
  }

  preSaveTranslationKey(key: string, value: string, lang: string): string {
    return value;
  }

  private flattenObject(obj: Record<string, unknown>, prefix = ''): Record<string, unknown> {
    return Object.keys(obj).reduce(
      (acc, k) => {
        const pre = prefix.length ? prefix + '.' : '';
        const val = obj[k];
        if (typeof val === 'object' && obj[k] !== null) {
          Object.assign(acc, this.flattenObject(val as Record<string, unknown>, pre + k));
        } else {
          acc[pre + k] = obj[k];
        }
        return acc;
      },
      {} as Record<string, unknown>,
    );
  }
}

export const scopeTranslocoInterceptor = {
  provide: TRANSLOCO_INTERCEPTOR,
  useClass: TranslocoScopeInterceptor,
};

export function provideTranslation(scope: string, alias = scope): Provider {
  return {
    provide: TRANSLOCO_SCOPE,
    useValue: { scope, alias },
    multi: true,
  };
}
